import React from 'react';
import { Search, Filter, SortAsc, SortDesc } from 'lucide-react';
import { BLOG_CATEGORIES } from '../../data/blog/categories';
import type { BlogFilter } from '../../types/blog';

interface BlogFiltersProps {
  filters: BlogFilter;
  onFiltersChange: (filters: BlogFilter) => void;
}

export function BlogFilters({ filters, onFiltersChange }: BlogFiltersProps) {
  const handleSearchChange = (search: string) => {
    onFiltersChange({ ...filters, search: search || undefined });
  };

  const handleCategoryChange = (category: string) => {
    onFiltersChange({ 
      ...filters, 
      category: category === 'all' ? undefined : category 
    });
  };

  const handleSortChange = (sortBy: 'date' | 'views' | 'title') => {
    const newOrder = filters.sortBy === sortBy && filters.sortOrder === 'desc' ? 'asc' : 'desc';
    onFiltersChange({ ...filters, sortBy, sortOrder: newOrder });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search articles..."
            value={filters.search || ''}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg 
                     focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>

        {/* Category Filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={filters.category || 'all'}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 
                     focus:ring-emerald-500 focus:border-emerald-500"
          >
            <option value="all">All Categories</option>
            {BLOG_CATEGORIES.map((category) => (
              <option key={category.id} value={category.slug}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Sort Options */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Sort by:</span>
          <div className="flex gap-1">
            {[
              { key: 'date', label: 'Date' },
              { key: 'views', label: 'Views' },
              { key: 'title', label: 'Title' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleSortChange(key as any)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                  filters.sortBy === key
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {label}
                {filters.sortBy === key && (
                  filters.sortOrder === 'desc' ? 
                    <SortDesc className="h-4 w-4" /> : 
                    <SortAsc className="h-4 w-4" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}